import { i18next } from 'core';
import { Loading } from 'ui';
import * as coreEntities from 'core/store/index';
import { getLanguageByTenant } from 'core/utils/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInterfaceLanguageIds, getLanguageWithBundle, importLanguageResources } from '../utils/languages';

/**
 * TranslationsProvider
 *
 * Provider que carga las traducciones según el tenant del usuario.
 * Extrae la lógica de carga de traducciones desde los Routes para centralizarla a nivel de App.
 *
 * Funcionalidad:
 * - Lee el tenant, el idioma del usuario, los idiomas de interfaz del tenant y el idioma activo
 *   desde Redux
 * - Coerciona el idioma del usuario a los idiomas de interfaz disponibles del tenant
 * - Carga los recursos de traducciones basándose en el tenant usando importLanguageResources
 * - Inyecta las traducciones en i18next
 * - Cambia el idioma activo en i18next y Redux
 * - Muestra un loading mientras las traducciones se cargan
 *
 * Sobre el momento de la coerción: la lista de idiomas de interfaz llega por `GET /langs`
 * después de autenticar, así que al arrancar todavía no se conoce. El provider no bloquea el
 * arranque esperándola (la petición la lanzan los Routes, que son hijos de este provider):
 * resuelve con lo que sabe y reacciona cuando la lista llega.
 *
 * Las dos decisiones están separadas a propósito:
 * - La inyección de bundles en i18next se hace una sola vez por par tenant/idioma (`ref`), que es
 *   lo caro y lo que no hace falta repetir.
 * - El cambio de idioma se decide contra el idioma efectivo (Redux + i18next), no contra el `ref`:
 *   el idioma de Redux se reescribe con el `lang_id` crudo del usuario cada vez que se recarga el
 *   usuario (`UserLoaded`, que dispara también el polling de sincronización), así que la coerción
 *   tiene que poder volver a imponerse en cualquier momento de la sesión.
 */

interface TranslationsProviderProps {
  children: React.ReactNode;
}

type Tenant = { guid?: string; name?: string; lang_id?: string };

/** Bundles por idioma: cada idioma trae una lista de objetos `{ namespace: { clave: texto } }`. */
type LanguageBundles = Record<string, Array<Record<string, Record<string, string>>>>;

/**
 * Inyecta en i18next los bundles del tenant para todos los idiomas disponibles.
 */
function addTranslationResources(tenant: Tenant | null, language: string): void {
  const languageKeys: LanguageBundles = importLanguageResources(tenant, language);

  Object.keys(languageKeys).forEach((lang) => {
    // Verificar que existan traducciones para este idioma
    if (Array.isArray(languageKeys[lang])) {
      languageKeys[lang].forEach((langScope) => {
        Object.keys(langScope).forEach((langScopeNs) => {
          i18next.addResources(lang, langScopeNs, langScope[langScopeNs]);
        });
      });
    } else {
      console.warn(`[TranslationsProvider] No translations found for language "${lang}", skipping.`);
    }
  });
}

function TranslationsProvider({ children }: TranslationsProviderProps): JSX.Element {
  const dispatch = useDispatch();
  const [translationsLoaded, setTranslationsLoaded] = useState<boolean>(false);
  // Par tenant/idioma cuyos bundles ya se han inyectado en i18next. Solo evita reinyectar
  // recursos; no decide el idioma activo (eso se compara contra el estado efectivo).
  const appliedLanguageKeyRef = useRef<string | null>(null);

  // Selectores de Redux
  const tenant: Tenant | null = useSelector((state: any) => coreEntities.organization.selectors.getTenant(state));
  const userLangId = useSelector((state: any) => state.auth?.user?.lang_id);
  const tenantLangs = useSelector((state: any) => coreEntities.langs.selectors.getLangs(state));
  const currentLanguage = useSelector((state: any) => coreEntities.i18n.selectors.getLanguage(state));

  // Idiomas de interfaz disponibles del tenant con bundle de traducción. Vacío mientras
  // `GET /langs` no ha respondido: en ese caso no se coerciona nada.
  const availableLanguages = useMemo<string[]>(() => getInterfaceLanguageIds(tenantLangs, tenant), [tenantLangs, tenant]);

  useEffect(() => {
    if (!tenant?.guid) {
      dispatch(coreEntities.organization.actions.getOrganization());
      return;
    }

    try {
      // 1. Resolver el idioma: forzado por tenant, idioma EFECTIVO o idioma del tenant,
      //    coercionado a los idiomas de interfaz disponibles cuando ya se conocen.
      //    `currentLanguage` va primero: es el idioma en vigor (incluida una elección recién
      //    guardada en Preferencias, que aún no está en `auth.user.lang_id` hasta que resuelve
      //    el PUT) — resolver desde `userLangId` la revertía en pleno vuelo (review PR #1095).
      //    La coerción sigue siendo autoritativa: un `UserLoaded` que reescriba el idioma con
      //    el `lang_id` crudo también reescribe `currentLanguage`, y se re-coerciona aquí.
      const resolvedLanguage = getLanguageByTenant(tenant?.guid, currentLanguage || userLangId, tenant?.lang_id, availableLanguages);

      // 2. Garantizar que el idioma resuelto tiene bundle en este tenant, para no caer al fallbackLng
      const defaultLanguage = getLanguageWithBundle(resolvedLanguage, tenant?.lang_id, tenant);

      // 3. Inyectar los bundles solo la primera vez para cada par tenant/idioma. El ref se marca
      //    al final: si la inyección lanza, el par no queda latcheado y se reintenta.
      const languageKey = `${tenant?.guid}|${defaultLanguage}`;
      if (appliedLanguageKeyRef.current !== languageKey) {
        addTranslationResources(tenant, defaultLanguage);
        appliedLanguageKeyRef.current = languageKey;
      }

      // 4. Aplicar el idioma solo donde el estado efectivo no sea ya el resuelto. Cada acción
      //    se condiciona a su propio lado: re-emitir `changeLanguage` con el mismo idioma
      //    dispara `languageChanged` y re-renderiza los componentes traducidos en cada
      //    `UserLoaded` (review PR #1095). Comparar contra el estado (y no contra el ref)
      //    mantiene la coerción auto-reparable.
      if (i18next.language !== defaultLanguage) {
        i18next.changeLanguage(defaultLanguage);
      }
      if (currentLanguage !== defaultLanguage) {
        dispatch(coreEntities.i18n.actions.setLanguage(defaultLanguage));
      }

      // 5. Marcar las traducciones como cargadas
      setTranslationsLoaded(true);
    } catch (error) {
      console.error('Error loading translations:', error);
      // En caso de error, igualmente marcamos como cargado para no bloquear la app
      setTranslationsLoaded(true);
    }
  }, [tenant?.guid, tenant?.lang_id, userLangId, availableLanguages, currentLanguage, dispatch]);

  // Si no hay tenant aún, no mostramos nada (esperamos a que se cargue)
  if (!tenant?.guid) {
    return <Loading />;
  }

  // Si las traducciones no están cargadas aún, mostramos loading
  if (!translationsLoaded) {
    return <Loading />;
  }

  // Renderizamos los children cuando las traducciones están cargadas
  return <>{children}</>;
}

export default TranslationsProvider;
